export class ValidationError extends Error {
  readonly path: string;

  constructor(path: string, message: string) {
    super(`${path}: ${message}`);
    this.name = "ValidationError";
    this.path = path;
  }
}

export function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function requireObject(path: string, value: unknown): Record<string, unknown> {
  if (!isObject(value)) {
    throw new ValidationError(path, "expected object");
  }
  return value;
}

export function requireNonEmptyString(path: string, value: unknown): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new ValidationError(path, "expected non-empty string");
  }
  return value;
}

export function requireBoolean(path: string, value: unknown): boolean {
  if (typeof value !== "boolean") {
    throw new ValidationError(path, "expected boolean");
  }
  return value;
}

export function requireInt(path: string, value: unknown, min?: number, max?: number): number {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    throw new ValidationError(path, "expected integer");
  }
  if (min !== undefined && value < min) {
    throw new ValidationError(path, `expected >= ${min}`);
  }
  if (max !== undefined && value > max) {
    throw new ValidationError(path, `expected <= ${max}`);
  }
  return value;
}

export function requireMilli(path: string, value: unknown, min = 0, max = 10000): number {
  return requireInt(path, value, min, max);
}

export function requireArray(path: string, value: unknown): unknown[] {
  if (!Array.isArray(value)) {
    throw new ValidationError(path, "expected array");
  }
  return value;
}

export function uniqueIds(path: string, ids: string[]): void {
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) {
      throw new ValidationError(path, `duplicate id "${id}"`);
    }
    seen.add(id);
  }
}

export function requireStringArray(path: string, value: unknown): string[] {
  const arr = requireArray(path, value);
  return arr.map((item, i) => requireNonEmptyString(`${path}[${i}]`, item));
}
